import './InvoiceTable.css'
import React, { Fragment, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import moment from 'moment'
import {
  Grid,
  GridColumn as Column,
  GridColumnMenuWrapper,
  GridColumnMenuCheckboxFilter,
} from '@progress/kendo-react-grid'

import Box from '@mui/material/Box'
import ThreeDotsLoadingSpinner from 'components/Common/LoadingSpinner/threeDots'

import api from 'api'
import { formatCurrency } from 'utils/user'

const ReceivableTable = () => {
  const [filterState, setFilterState] = useState(null)

  const dayspanSelected = useSelector(state => state.appReducer.financialOverviewDaySpan)
  const companyId = useSelector(state => state.apiResponseReducers.companyId)

  // eslint-disable-next-line
  const { records, totalCount, pageNo, pageSize, sortBy, desc, status } = useSelector(
    state => state.apiResponseReducers.paidInvoices,
  )
  const loadingPaidInvoicesData = useSelector(state => state.apiResponseReducers.loadingPaidInvoicesData)
  const paidInvoices = useSelector(state => state.apiResponseReducers.paidInvoices)
  const [prevDayspanSelected, setPrevDayspanSelected] = useState(dayspanSelected)

  useEffect(() => {
    // Initiate the table
    if (!companyId || !companyId.length || !dayspanSelected) return

    // Reset page no if dayspan is changed
    const newPageNo = prevDayspanSelected === dayspanSelected ? pageNo : 1
    setPrevDayspanSelected(dayspanSelected)

    api.getPaidInvoicesTableData(companyId, dayspanSelected, newPageNo, pageSize, sortBy, desc, ['Paid'])

    // Get filter options
    // api.getReceivableInvoiceStatusFilter().then(res => {
    //   res = res?.map(e => ({ status: e }))
    //   setStatusFilterOptions(res)
    // })
    // NOTE: do not add dependency from [newPageNo, pageSize, sortBy, desc, status] to avoid unncecessary api call
    // eslint-disable-next-line
  }, [companyId, dayspanSelected])

  const onSort = newSortBy => {
    api.getPaidInvoicesTableData(
      companyId,
      dayspanSelected,
      pageNo,
      pageSize,
      newSortBy,
      sortBy === newSortBy ? !desc : true,
      ['Paid'],
    )
  }

  const filterChange = state => {
    let status = ['Paid']
    let filters = []
    if (state?.filter?.filters) {
      filters = state?.filter?.filters
    }

    if (filters?.length > 0) {
      status = filters[0].filters.map(filter => filter.value)
      status = status.join(',')
    }
    api
      .getPaidInvoicesTableData(companyId, dayspanSelected, undefined, undefined, undefined, undefined, status)
      .then(() => {
        setFilterState(state?.filter)
      })
  }

  const onPageChange = event => {
    if (!companyId || !companyId.length || !dayspanSelected) return
    api.getPaidInvoicesTableData(
      companyId,
      dayspanSelected,
      event.page.skip / event.page.take + 1,
      event.page.take,
      sortBy,
      desc,
      ['Paid'],
    )
  }

  if (!sortBy || !sortBy.length) {
    return <Fragment></Fragment>
  }

  return (
    <div className="fo-invoice-table receivable-table">
      <div className="fo-table-header">
        <span className="fo-table-title">Paid Invoices</span>
      </div>
      {loadingPaidInvoicesData && (
        <Box display="flex" alignItems="center" justifyContent="center">
          <ThreeDotsLoadingSpinner width="60px" />
        </Box>
      )}
      {!loadingPaidInvoicesData && (
        <div className="fo-table-wrapper">
          <Grid
            style={{
              maxHeight: '300px',
            }}
            onPageChange={onPageChange}
            data={records}
            total={totalCount}
            skip={(pageNo - 1) * pageSize}
            pageable={{
              buttonCount: 5,
              info: true,
              type: 'numeric',
              pageSizes: true,
              previousNext: true,
            }}
            pageSize={pageSize}
            onFilterChange={filterChange}
          >
            <Column
              field="due_date"
              headerCell={() => (
                <span onClick={() => onSort('due_date')}>
                  Date Due {sortBy === 'due_date' ? (desc ? '⬇' : '⬆') : ''}
                </span>
              )}
              cell={props => <td>{moment(props.dataItem[props.field]).format('MMM Do, YYYY')}</td>}
              width="110px"
            />
            <Column
              field="issue_date"
              headerCell={() => (
                <span onClick={() => onSort('issue_date')}>
                  Date Issued {sortBy === 'issue_date' ? (desc ? '⬇' : '⬆') : ''}
                </span>
              )}
              cell={props => <td>{moment(props.dataItem[props.field]).format('MMM Do, YYYY')}</td>}
              width="110px"
            />
            <Column
              field="paid_on_date"
              headerCell={() => (
                <span onClick={() => onSort('paid_on_date')}>
                  Date Paid {sortBy === 'paid_on_date' ? (desc ? '⬇' : '⬆') : ''}
                </span>
              )}
              cell={props => (
                <td>
                  {props.dataItem[props.field] ? moment(props.dataItem[props.field]).format('MMM Do, YYYY') : '-'}
                </td>
              )}
              width="110px"
            />
            <Column
              field="customer_name"
              headerCell={() => (
                <span onClick={() => onSort('customer_name')}>
                  Customer Name {sortBy === 'customer_name' ? (desc ? '⬇' : '⬆') : ''}
                </span>
              )}
              cell={props => <td>{props.dataItem[props.field] || 'N/A'}</td>}
              width="140px"
            />
            <Column
              field="total_amount"
              headerCell={() => (
                <span onClick={() => onSort('total_amount')}>
                  Total Amount ($) {sortBy === 'total_amount' ? (desc ? '⬇' : '⬆') : ''}
                </span>
              )}
              cell={props => (
                <td>{`${formatCurrency(props.dataItem[props.field])}  ${props?.dataItem['currency'] || 'USD'}`}</td>
              )}
              width="140px"
            />
            <Column
              field="amount_due"
              headerCell={() => (
                <span onClick={() => onSort('amount_due')}>
                  Amount Due ($) {sortBy === 'amount_due' ? (desc ? '⬇' : '⬆') : ''}
                </span>
              )}
              cell={props => (
                <td>{`${formatCurrency(props.dataItem[props.field])}  ${props?.dataItem['currency'] || 'USD'}`}</td>
              )}
              width="140px"
            />

            <Column
              field="invoice_number"
              headerCell={() => (
                <span
                // onClick={() => onSort('invoice_number')}
                >
                  Invoice number
                  {/* {sortBy === 'invoice_number' ? (desc ? '⬇' : '⬆') : ''} */}
                </span>
              )}
              cell={props => <td>{props.dataItem[props.field]}</td>}
              width="140px"
            />

            <Column
              field="status"
              columnMenu={props => (
                <GridColumnMenuCheckboxFilter
                  {...props}
                  // data={statusFilterOptions}
                  data={paidInvoices?.filterTypes.map(f => {
                    return { status: f }
                  })}
                  expanded={true}
                  filter={filterState}
                />
              )}
              headerCell={props => {
                return (
                  <>
                    <span>Status</span>
                    <GridColumnMenuWrapper {...props.columnMenuWrapperProps} />
                  </>
                )
              }}
              cell={props => <td>{props.dataItem[props.field]}</td>}
              width="100px"
            />
          </Grid>
        </div>
      )}
    </div>
  )
}
export default ReceivableTable
